import React, { useContext, useEffect, useState } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Tabs, Tab, Button, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Select, MenuItem, FormControl, InputLabel, Drawer, IconButton,
    Divider, CircularProgress, Stack, Tooltip, List, ListItem,
    ListItemText, Paper, FormHelperText
} from '@mui/material';
import { DataGrid, frFR } from '@mui/x-data-grid';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../services/API';
import { UserContext } from '../services/UserContext';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';

// ─── Constantes ──────────────────────────────────────────────────────────────

const STATUTS = ['A contacter', 'En attente', 'A rappeler plus tard', 'Prochain mandat', 'Décliné'];
const TYPES_ECHANGE = ['Appel', 'Email', 'Autre'];

const EMPTY_CONTACT = { ville: '', statut: 'A contacter', rappeler_le: null, telephone: '', mail: '', ouvertures: '' };
const EMPTY_ECHANGE = { type: 'Appel', contenu: '', date: null };
const EMPTY_TEMPLATE = { nom: '', sujet: '', corps: '' };

function statutChip(statut) {
    const props = { label: statut, size: 'small' };
    if (statut === 'A contacter') return <Chip {...props} color="info" />;
    if (statut === 'En attente') return <Chip {...props} color="warning" />;
    if (statut === 'Prochain mandat') return <Chip {...props} sx={{ backgroundColor: '#F7DEF3', color: '#8B3E7F', fontWeight: 600 }} />;
    if (statut === 'Décliné') return <Chip {...props} color="error" />;
    return <Chip {...props} />;
}

function interpolate(str, contact) {
    return (str || '')
        .replace(/\{\{ville\}\}/g, contact?.ville || '')
        .replace(/\{\{telephone\}\}/g, contact?.telephone || '')
        .replace(/\{\{mail\}\}/g, contact?.mail || '');
}


// ─── CRM principal ───────────────────────────────────────────────────────────

export default function CRM() {
    const api = useAPI();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [userContext] = useContext(UserContext);

    const [tab, setTab] = useState(0);
    const [contacts, setContacts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);

    // Drawer détail contact
    const [drawer, setDrawer] = useState({ open: false, contact: null, echanges: [], loadingEchanges: false });

    // Dialog contact (create/edit)
    const [cDialog, setCDialog] = useState({ open: false, mode: 'create', form: EMPTY_CONTACT, saving: false });

    // Dialog échange (create/edit)
    const [eDialog, setEDialog] = useState({ open: false, mode: 'create', form: EMPTY_ECHANGE, id: null, saving: false });

    // Dialog email
    const [emailDialog, setEmailDialog] = useState({ open: false, contact: null, sujet: '', corps: '', sending: false });

    // Dialog template (create/edit)
    const [tDialog, setTDialog] = useState({ open: false, mode: 'create', form: EMPTY_TEMPLATE, id: null, saving: false });

    // ── Chargement des données ────────────────────────────────────────────────

    const loadContacts = async () => {
        setLoading(true);
        try {
            const res = await api('GET', 'crm/contact');
            if (res.ok) setContacts(res.json);
        } finally {
            setLoading(false);
        }
    };

    const loadTemplates = async () => {
        const res = await api('GET', 'crm/template');
        if (res.ok) setTemplates(res.json);
    };

    const loadEchanges = async (contactId) => {
        setDrawer(d => ({ ...d, loadingEchanges: true }));
        const res = await api('GET', `crm/contact/${contactId}/echange`);
        setDrawer(d => ({ ...d, echanges: res.ok ? res.json : [], loadingEchanges: false }));
    };

    useEffect(() => { loadContacts(); loadTemplates(); }, []);

    // ── Contacts CRUD ─────────────────────────────────────────────────────────

    const openContactDialog = (mode, contact = null) => {
        setCDialog({
            open: true,
            mode,
            form: contact ? { ...contact, rappeler_le: contact.rappeler_le || null } : EMPTY_CONTACT,
            id: contact?.id || null,
            saving: false
        });
    };

    const handleSaveContact = async () => {
        if (!cDialog.form.ville.trim()) return enqueueSnackbar('La ville est obligatoire', { variant: 'error' });
        setCDialog(d => ({ ...d, saving: true }));
        const method = cDialog.mode === 'create' ? 'POST' : 'PUT';
        const path = cDialog.mode === 'create' ? 'crm/contact' : `crm/contact/${cDialog.id}`;
        const res = await api(method, path, cDialog.form);
        setCDialog(d => ({ ...d, saving: false }));
        if (res.ok) {
            enqueueSnackbar(`Contact ${cDialog.mode === 'create' ? 'créé' : 'modifié'}`, { variant: 'success' });
            setCDialog(d => ({ ...d, open: false }));
            if (cDialog.mode === 'edit' && drawer.open && drawer.contact?.id === cDialog.id) {
                setDrawer(d => ({ ...d, contact: res.json }));
            }
            loadContacts();
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur lors de la sauvegarde', { variant: 'error' });
        }
    };

    const handleDeleteContact = async (id) => {
        if (!window.confirm('Supprimer ce contact et tous ses échanges ?')) return;
        const res = await api('DELETE', `crm/contact/${id}`);
        if (res.ok || res.status === 204) {
            enqueueSnackbar('Contact supprimé', { variant: 'success' });
            if (drawer.open && drawer.contact?.id === id) setDrawer(d => ({ ...d, open: false }));
            loadContacts();
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur lors de la suppression', { variant: 'error' });
        }
    };

    // ── Drawer détail ─────────────────────────────────────────────────────────

    const openDrawer = (contact) => {
        setDrawer({ open: true, contact, echanges: [], loadingEchanges: true });
        loadEchanges(contact.id);
    };

    // ── Échanges CRUD ─────────────────────────────────────────────────────────

    const openEchangeDialog = (mode, echange = null) => {
        setEDialog({
            open: true,
            mode,
            form: echange ? { type: echange.type, contenu: echange.contenu, date: echange.date } : EMPTY_ECHANGE,
            id: echange?.id || null,
            saving: false
        });
    };

    const handleSaveEchange = async () => {
        if (!eDialog.form.contenu.trim()) return enqueueSnackbar('Le contenu est obligatoire', { variant: 'error' });
        setEDialog(d => ({ ...d, saving: true }));
        let res;
        if (eDialog.mode === 'create') {
            res = await api('POST', `crm/contact/${drawer.contact.id}/echange`, {
                ...eDialog.form,
                date: eDialog.form.date || new Date().toISOString()
            });
        } else {
            res = await api('PUT', `crm/echange/${eDialog.id}`, {
                ...eDialog.form,
                date: eDialog.form.date || new Date().toISOString()
            });
        }
        setEDialog(d => ({ ...d, saving: false }));
        if (res.ok || res.status === 201) {
            enqueueSnackbar(`Échange ${eDialog.mode === 'create' ? 'ajouté' : 'modifié'}`, { variant: 'success' });
            setEDialog(d => ({ ...d, open: false }));
            loadEchanges(drawer.contact.id);
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur lors de la sauvegarde', { variant: 'error' });
        }
    };

    const handleDeleteEchange = async (id) => {
        if (!window.confirm('Supprimer cet échange ?')) return;
        const res = await api('DELETE', `crm/echange/${id}`);
        if (res.ok || res.status === 204) {
            enqueueSnackbar('Échange supprimé', { variant: 'success' });
            loadEchanges(drawer.contact.id);
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur', { variant: 'error' });
        }
    };

    // ── Email ─────────────────────────────────────────────────────────────────

    const openEmailDialog = (contact) => {
        setEmailDialog({ open: true, contact, sujet: '', corps: '', selectedTemplate: '', sending: false });
    };

    const handleTemplateChange = (templateId) => {
        const tpl = templates.find(t => t.id === templateId);
        if (!tpl) return;
        setEmailDialog(d => ({
            ...d,
            selectedTemplate: templateId,
            sujet: interpolate(tpl.sujet, d.contact),
            corps: interpolate(tpl.corps, d.contact)
        }));
    };

    const handleSendEmail = async () => {
        if (!emailDialog.sujet.trim()) return enqueueSnackbar('Le sujet est obligatoire', { variant: 'error' });
        if (!emailDialog.corps.trim()) return enqueueSnackbar('Le corps est obligatoire', { variant: 'error' });
        setEmailDialog(d => ({ ...d, sending: true }));
        const res = await api('POST', `crm/contact/${emailDialog.contact.id}/envoyer-email`, {
            sujet_final: emailDialog.sujet,
            corps_final: emailDialog.corps
        });
        setEmailDialog(d => ({ ...d, sending: false }));
        if (res.ok || res.status === 201) {
            enqueueSnackbar(`Email envoyé à ${emailDialog.contact.ville}`, { variant: 'success' });
            setEmailDialog(d => ({ ...d, open: false }));
            if (drawer.open && drawer.contact?.id === emailDialog.contact.id) {
                loadEchanges(drawer.contact.id);
            }
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur lors de l\'envoi', { variant: 'error' });
        }
    };

    // ── Templates CRUD ────────────────────────────────────────────────────────

    const openTemplateDialog = (mode, template = null) => {
        setTDialog({
            open: true,
            mode,
            form: template ? { nom: template.nom, sujet: template.sujet, corps: template.corps } : EMPTY_TEMPLATE,
            id: template?.id || null,
            saving: false
        });
    };

    const handleSaveTemplate = async () => {
        const { nom, sujet, corps } = tDialog.form;
        if (!nom.trim()) return enqueueSnackbar('Le nom est obligatoire', { variant: 'error' });
        if (!sujet.trim()) return enqueueSnackbar('Le sujet est obligatoire', { variant: 'error' });
        if (!corps.trim()) return enqueueSnackbar('Le corps est obligatoire', { variant: 'error' });
        setTDialog(d => ({ ...d, saving: true }));
        const method = tDialog.mode === 'create' ? 'POST' : 'PUT';
        const path = tDialog.mode === 'create' ? 'crm/template' : `crm/template/${tDialog.id}`;
        const res = await api(method, path, tDialog.form);
        setTDialog(d => ({ ...d, saving: false }));
        if (res.ok || res.status === 201) {
            enqueueSnackbar(`Modèle ${tDialog.mode === 'create' ? 'créé' : 'modifié'}`, { variant: 'success' });
            setTDialog(d => ({ ...d, open: false }));
            loadTemplates();
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur lors de la sauvegarde', { variant: 'error' });
        }
    };

    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('Supprimer ce modèle ?')) return;
        const res = await api('DELETE', `crm/template/${id}`);
        if (res.ok || res.status === 204) {
            enqueueSnackbar('Modèle supprimé', { variant: 'success' });
            loadTemplates();
        } else {
            enqueueSnackbar(res.json?.messageFr || 'Erreur', { variant: 'error' });
        }
    };

    // ── Colonnes DataGrid ─────────────────────────────────────────────────────

    const columns = [
        { field: 'ville', headerName: 'Ville', width: 160, flex: 1 },
        {
            field: 'statut', headerName: 'Statut', width: 200,
            renderCell: ({ value }) => statutChip(value)
        },
        {
            field: 'rappeler_le', headerName: 'Rappeler le', width: 200,
            renderCell: ({ value }) => {
                if (!value) return <span style={{ color: '#aaa' }}>—</span>;
                const isLate = dayjs(value).isBefore(dayjs(), 'day');
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{dayjs(value).format('DD/MM/YYYY')}</span>
                        {isLate && <Chip label="En retard" color="error" size="small" />}
                    </Box>
                );
            }
        },
        { field: 'telephone', headerName: 'Téléphone', width: 150 },
        { field: 'mail', headerName: 'Email', width: 200 },
        {
            field: 'actions', headerName: 'Actions', width: 180, sortable: false, filterable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Voir détails">
                        <IconButton size="small" onClick={() => openDrawer(row)}><VisibilityIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={row.mail ? 'Envoyer un email' : 'Pas d\'email renseigné'}>
                        <span>
                            <IconButton size="small" disabled={!row.mail} onClick={() => openEmailDialog(row)}>
                                <EmailIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => openContactDialog('edit', row)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDeleteContact(row.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    // ── Rendu ─────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#F6F6F9' }}>

            {/* AppBar */}
            <AppBar position="static" color="primary">
                <Toolbar>
                    <Tooltip title={`Retour à ${userContext.commune || 'l\'application'}`}>
                        <IconButton color="inherit" edge="start" sx={{ mr: 2 }}
                            onClick={() => navigate(userContext.commune ? `/${userContext.commune}` : '/login')}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>CRM – Prospection mairies</Typography>
                </Toolbar>
            </AppBar>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
                    <Tab label="Contacts" />
                    <Tab label="Modèles d'emails" />
                </Tabs>
            </Box>

            {/* Panel Contacts */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: tab === 0 ? 'flex' : 'none', flexDirection: 'column', p: 2 }}>
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary.main">
                        {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openContactDialog('create')}>
                        Nouveau contact
                    </Button>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <DataGrid
                        rows={contacts}
                        columns={columns}
                        loading={loading}
                        sx={{ height: '100%', bgcolor: 'white', borderRadius: 2 }}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        disableRowSelectionOnClick
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        getRowId={(row) => row.id}
                    />
                </Box>
            </Box>

            {/* Panel Templates */}
            <Box sx={{ flex: 1, overflow: 'auto', display: tab === 1 ? 'block' : 'none', p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary.main">Modèles d'emails</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openTemplateDialog('create')}>
                        Nouveau modèle
                    </Button>
                </Box>
                {templates.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        Aucun modèle. Créez-en un pour envoyer des emails semi-automatiques.
                    </Paper>
                ) : (
                    <Stack spacing={1.5}>
                        {templates.map(tpl => (
                            <Paper key={tpl.id} sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography fontWeight={700}>{tpl.nom}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Sujet : {tpl.sujet}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => openTemplateDialog('edit', tpl)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(tpl.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>


            {/* ── Drawer détail contact ──────────────────────────────────────── */}
            <Drawer anchor="right" open={drawer.open} onClose={() => setDrawer(d => ({ ...d, open: false }))}
                PaperProps={{ sx: { width: { xs: '100vw', sm: 500 } } }}>
                {drawer.contact && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {/* Header */}
                        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography variant="h6" fontWeight={700}>{drawer.contact.ville}</Typography>
                                <Box sx={{ mt: 0.5 }}>{statutChip(drawer.contact.statut)}</Box>
                            </Box>
                            <IconButton color="inherit" onClick={() => setDrawer(d => ({ ...d, open: false }))}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Infos contact */}
                        <Box sx={{ p: 2, bgcolor: '#f5f5f8' }}>
                            <Stack spacing={0.5}>
                                {drawer.contact.rappeler_le && (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>Rappeler le :</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {dayjs(drawer.contact.rappeler_le).format('DD/MM/YYYY')}
                                        </Typography>
                                        {dayjs(drawer.contact.rappeler_le).isBefore(dayjs(), 'day') &&
                                            <Chip label="En retard" color="error" size="small" />}
                                    </Box>
                                )}
                                {drawer.contact.telephone && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>Téléphone :</Typography>
                                        <Typography variant="body2">{drawer.contact.telephone}</Typography>
                                    </Box>
                                )}
                                {drawer.contact.mail && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>Email :</Typography>
                                        <Typography variant="body2">{drawer.contact.mail}</Typography>
                                    </Box>
                                )}
                                {drawer.contact.ouvertures && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>Horaires :</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{drawer.contact.ouvertures}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Boutons actions */}
                        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, borderBottom: '1px solid #eee' }}>
                            <Button size="small" variant="outlined" startIcon={<EditIcon />}
                                onClick={() => openContactDialog('edit', drawer.contact)}>
                                Modifier
                            </Button>
                            <Button size="small" variant="outlined" color="secondary" startIcon={<EmailIcon />}
                                disabled={!drawer.contact.mail}
                                onClick={() => openEmailDialog(drawer.contact)}>
                                Envoyer email
                            </Button>
                        </Box>

                        {/* Historique échanges */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography fontWeight={700} color="primary.main">Historique des échanges</Typography>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => openEchangeDialog('create')}>
                                    Ajouter
                                </Button>
                            </Box>

                            {drawer.loadingEchanges ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : drawer.echanges.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    Aucun échange enregistré
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {drawer.echanges.map(e => (
                                        <Paper key={e.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {dayjs(e.date).format('DD/MM/YYYY HH:mm')}
                                                    </Typography>
                                                    <Chip label={e.type} size="small"
                                                        color={e.type === 'Appel' ? 'info' : e.type === 'Email' ? 'success' : 'default'} />
                                                </Box>
                                                <Box sx={{ display: 'flex' }}>
                                                    <IconButton size="small" onClick={() => openEchangeDialog('edit', e)}>
                                                        <EditIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDeleteEchange(e.id)}>
                                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                                                {e.contenu}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>


            {/* ── Dialog Contact (create/edit) ───────────────────────────────── */}
            <Dialog open={cDialog.open} onClose={() => setCDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
                <DialogTitle>{cDialog.mode === 'create' ? 'Nouveau contact' : 'Modifier le contact'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label="Ville *" size="small" fullWidth
                        value={cDialog.form.ville}
                        onChange={e => setCDialog(d => ({ ...d, form: { ...d.form, ville: e.target.value } }))}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Statut</InputLabel>
                        <Select label="Statut" value={cDialog.form.statut}
                            onChange={e => setCDialog(d => ({ ...d, form: { ...d.form, statut: e.target.value } }))}>
                            {STATUTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <DatePicker
                        label="Rappeler le"
                        value={cDialog.form.rappeler_le ? dayjs(cDialog.form.rappeler_le) : null}
                        onChange={val => setCDialog(d => ({ ...d, form: { ...d.form, rappeler_le: val ? val.format('YYYY-MM-DD') : null } }))}
                        slotProps={{
                            textField: { size: 'small', fullWidth: true },
                            actionBar: { actions: ['clear', 'accept'] }
                        }}
                    />
                    <TextField
                        label="Téléphone" size="small" fullWidth
                        value={cDialog.form.telephone}
                        onChange={e => setCDialog(d => ({ ...d, form: { ...d.form, telephone: e.target.value } }))}
                    />
                    <TextField
                        label="Email" size="small" fullWidth type="email"
                        value={cDialog.form.mail}
                        onChange={e => setCDialog(d => ({ ...d, form: { ...d.form, mail: e.target.value } }))}
                    />
                    <TextField
                        label="Horaires d'ouverture" size="small" fullWidth multiline rows={3}
                        value={cDialog.form.ouvertures}
                        onChange={e => setCDialog(d => ({ ...d, form: { ...d.form, ouvertures: e.target.value } }))}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCDialog(d => ({ ...d, open: false }))}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveContact} disabled={cDialog.saving}>
                        {cDialog.saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ── Dialog Échange (create/edit) ───────────────────────────────── */}
            <Dialog open={eDialog.open} onClose={() => setEDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
                <DialogTitle>{eDialog.mode === 'create' ? 'Ajouter un échange' : 'Modifier l\'échange'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <DateTimePicker
                        label="Date et heure"
                        value={eDialog.form.date ? dayjs(eDialog.form.date) : dayjs()}
                        onChange={val => setEDialog(d => ({ ...d, form: { ...d.form, date: val ? val.toISOString() : null } }))}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={eDialog.form.type}
                            onChange={e => setEDialog(d => ({ ...d, form: { ...d.form, type: e.target.value } }))}>
                            {TYPES_ECHANGE.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Contenu *" size="small" fullWidth multiline rows={4}
                        value={eDialog.form.contenu}
                        onChange={e => setEDialog(d => ({ ...d, form: { ...d.form, contenu: e.target.value } }))}
                        placeholder="Résumé de l'échange, notes, actions à faire..."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEDialog(d => ({ ...d, open: false }))}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveEchange} disabled={eDialog.saving}>
                        {eDialog.saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ── Dialog Envoi email ─────────────────────────────────────────── */}
            <Dialog open={emailDialog.open} onClose={() => !emailDialog.sending && setEmailDialog(d => ({ ...d, open: false }))}
                maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Envoyer un email – {emailDialog.contact?.ville}</span>
                    <Typography variant="body2" color="text.secondary">{emailDialog.contact?.mail}</Typography>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Modèle de départ</InputLabel>
                        <Select label="Modèle de départ"
                            value={emailDialog.selectedTemplate || ''}
                            onChange={e => handleTemplateChange(e.target.value)}>
                            <MenuItem value=""><em>Aucun (email libre)</em></MenuItem>
                            {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.nom}</MenuItem>)}
                        </Select>
                        <FormHelperText>Sélectionner un modèle pré-remplit les champs (modifiables ensuite)</FormHelperText>
                    </FormControl>
                    <TextField
                        label="Sujet *" size="small" fullWidth
                        value={emailDialog.sujet}
                        onChange={e => setEmailDialog(d => ({ ...d, sujet: e.target.value }))}
                    />
                    <TextField
                        label="Corps du message *" size="small" fullWidth multiline rows={10}
                        value={emailDialog.corps}
                        onChange={e => setEmailDialog(d => ({ ...d, corps: e.target.value }))}
                        placeholder="Contenu de l'email (HTML ou texte brut)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEmailDialog(d => ({ ...d, open: false }))} disabled={emailDialog.sending}>
                        Annuler
                    </Button>
                    <Button variant="contained" startIcon={emailDialog.sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        onClick={handleSendEmail} disabled={emailDialog.sending}>
                        {emailDialog.sending ? 'Envoi...' : 'Envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ── Dialog Template (create/edit) ─────────────────────────────── */}
            <Dialog open={tDialog.open} onClose={() => setTDialog(d => ({ ...d, open: false }))} maxWidth="md" fullWidth>
                <DialogTitle>{tDialog.mode === 'create' ? 'Nouveau modèle' : 'Modifier le modèle'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label="Nom du modèle *" size="small" fullWidth
                        value={tDialog.form.nom}
                        onChange={e => setTDialog(d => ({ ...d, form: { ...d.form, nom: e.target.value } }))}
                        placeholder="Ex: Envoi d'informations, Relance, Après refus..."
                    />
                    <TextField
                        label="Sujet *" size="small" fullWidth
                        value={tDialog.form.sujet}
                        onChange={e => setTDialog(d => ({ ...d, form: { ...d.form, sujet: e.target.value } }))}
                        helperText="Variables : {{ville}}, {{telephone}}, {{mail}}"
                    />
                    <TextField
                        label="Corps du message *" size="small" fullWidth multiline rows={12}
                        value={tDialog.form.corps}
                        onChange={e => setTDialog(d => ({ ...d, form: { ...d.form, corps: e.target.value } }))}
                        placeholder={`Bonjour,\n\nSuite à notre échange téléphonique concernant la mairie de {{ville}}, ...\n\nCordialement,`}
                        helperText="Variables disponibles : {{ville}}, {{telephone}}, {{mail}}"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTDialog(d => ({ ...d, open: false }))}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveTemplate} disabled={tDialog.saving}>
                        {tDialog.saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
